// src/components/Admin/PaymentVerification.js

import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Input, 
  Button, 
  Space, 
  DatePicker, 
  Form, 
  message, 
  Tag,
  Popconfirm,
  InputNumber
} from 'antd';
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { listPendingPayments, verifyPayment } from '../../services/api';
import DashboardLayout from '../Dashboard/DashboardLayout';
import AdminNotificationCenter from './AdminNotificationCenter';

const { RangePicker } = DatePicker;

const PaymentVerification = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async (filterParams = {}) => {
    setLoading(true);
    try {
      const data = await listPendingPayments(filterParams);
      setPayments(data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      message.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNext = async (paymentId, verified) => {
    try {
      await verifyPayment(paymentId, {
        status: verified ? 'completed' : 'failed'
      });
      message.success('Payment status updated successfully');
      fetchPayments(filters); // Refresh the list
    } catch (error) {
      console.error('Error updating payment status:', error);
      message.error('Failed to update payment status');
    }
  };
  // In your PaymentVerification component
const handleVerify = async (paymentId, verified) => {
  try {
    const result = await verifyPayment(paymentId, {
      status: verified ? 'completed' : 'failed'
    });
    
    if (result.verified) {
      if (result.domain_registered) {
        message.success(`Payment verified and domain ${result.domain_details.name} registered successfully!`);
      } else if (result.domain_name) {
        message.warning('Payment verified but domain registration may need attention.');
      } else {
        message.success('Payment verified successfully');
      }
    } else {
      message.error(result.message || 'Failed to verify payment');
    }
    
    await fetchPayments(filters); // Refresh the list
  } catch (error) {
    console.error('Error updating payment status:', error);
    message.error('Failed to update payment status');
  }
};

  const handleSearch = async (values) => {
    const { dateRange, ...rest } = values;
    const filterParams = {
      ...rest,
      date_from: dateRange?.[0]?.toISOString(),
      date_to: dateRange?.[1]?.toISOString()
    };
    setFilters(filterParams);
    await fetchPayments(filterParams);
  };

  const columns = [
    {
      title: 'Domain',
      dataIndex: 'domain_name',
      key: 'domain_name',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `₹${parseFloat(amount).toFixed(2)}`,
    },
    {
      title: 'UPI ID',
      dataIndex: 'upi_id',
      key: 'upi_id',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={
          status === 'completed' ? 'green' :
          status === 'failed' ? 'red' :
          'gold'
        }>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Verify Payment"
            description="Are you sure you want to verify this payment?"
            onConfirm={() => handleVerify(record.id, true)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="primary" 
              icon={<CheckCircleOutlined />}
              disabled={record.status === 'completed'}
            >
              Verify
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Mark as Failed"
            description="Are you sure you want to mark this payment as failed?"
            onConfirm={() => handleVerify(record.id, false)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              danger 
              icon={<CloseCircleOutlined />}
              disabled={record.status === 'failed'}
            >
              Failed
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <AdminNotificationCenter />
    <Card title="Payment Verification">
      <Form
        form={form}
        onFinish={handleSearch}
        layout="vertical"
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Form.Item name="domain" label="Domain">
            <Input placeholder="Search domain" />
          </Form.Item>
          <Form.Item name="upi_id" label="UPI ID">
            <Input placeholder="Search UPI ID" />
          </Form.Item>
          <Form.Item name="min_amount" label="Min Amount">
            <InputNumber placeholder="Min amount" />
          </Form.Item>
          <Form.Item name="max_amount" label="Max Amount">
            <InputNumber placeholder="Max amount" />
          </Form.Item>
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker showTime />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              Search
            </Button>
          </Form.Item>
        </Space>
      </Form>

      <Table
        columns={columns}
        dataSource={payments}
        rowKey="id"
        loading={loading}
      />
    </Card>
    </DashboardLayout>
  );
};

export default PaymentVerification;